import React, { useState, useMemo, useEffect, useContext, useCallback } from "react";
import { visuallyHidden } from "@mui/utils";
import {
  Box,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { messengerRequest } from "Auth/auth-config";
import config from "config";
import Socket from "Socket/socket.js";

import { MsalContext } from "Auth/msal-context";
import { getConfig, setNetworkConnection } from "../../../src/Dashboard/ducks/systemDuck";
import { fetchDevicesConfigs } from "../../../src/Dashboard/ducks/devicesConfigsDuck";
import { closeSocket } from "../../../src/Dashboard/ducks/socketDuck";
import { useSocketConnectionDevicesMessageTypeEvent } from "../../../src/Dashboard/helpers/useSocketConnection";
import { DevicesConfig, RefreshSignalR } from "../../../src/Dashboard/actions/apiActions";
import { refreshSignalR } from "../../../src/Dashboard/ducks/devicesDuck";
import { alarmsMessageTypeEvent } from "../../../src/Dashboard/constants/apiUrl";
import WarningIcon from "@mui/icons-material/Warning";
import { useTranslation } from "react-i18next";
import { useCloudDeviceConfigEntries, useCloudSocketConnected, useCloudRawSensorData } from '../../hooks/useCloudSelectors';
import { sensorTable } from "../../../src/theme/muiTheme";

// Table sorting functions
function descendingComparator(a, b, orderBy) {
  const aValue = typeof a[orderBy] === "object" && a[orderBy] !== null ? parseFloat(a[orderBy].value) : a[orderBy];
  const bValue = typeof b[orderBy] === "object" && b[orderBy] !== null ? parseFloat(b[orderBy].value) : b[orderBy];

  if (bValue < aValue) return -1;
  if (bValue > aValue) return 1;
  return 0;
}
function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Table head with sorting
function EnhancedTableHead({ headCells, order, orderBy, onRequestSort }) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            // padding={headCell.disablePadding ? "none" : "normal"}
            padding="none"
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              <Typography>{headCell.label}</Typography>
              {orderBy === headCell.id && (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              )}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const SensorTable = ({
  system,
  site,
  showSensorId,
  showTimestamp,
  sensorValueFilterMeasurement,
  sensorValueFilterOperator,
  sensorValueFilterValue,
  handleChangeMeasurementList,
  measurementNameList
}) => {

  const msalContext = useContext(MsalContext);
  const dispatch = useDispatch();
  const isConfigInitialized = useSelector(({ config: c }) => c[system.deviceId]?._initialized);
  const isRealtimeInitialized = useSelector(({ realtime }) => realtime[system.deviceId]?._initialized);

  const sensorConfigs = useCloudDeviceConfigEntries(system.deviceId);
  const socketConnected = useCloudSocketConnected(system.deviceId);
  const rawSensorData = useCloudRawSensorData(system.deviceId);
  // console.log("rawSensorData", rawSensorData);

  const [dataRows, setDataRows] = useState([]);
  // console.log("SensorTable dataRows", dataRows);
  const [alarms, setAlarms] = useState([]);

  //get current language
  const { i18n, t } = useTranslation();

  // Initialize Redux tiers (safety net — FetchSensorConfigs also does this)
  useEffect(() => {
    const payload = { systemId: system.deviceId, solutionType: system.solutionType };
    if (isConfigInitialized === false) {
      dispatch({ type: "config/initializeDeviceConfig", payload });
    }
    if (isRealtimeInitialized === false) {
      dispatch({ type: "realtime/initializeDeviceRealtime", payload });
    }
  }, [system.deviceId, isConfigInitialized, isRealtimeInitialized, dispatch]);

  // this makes the socket connection
  useEffect(() => {
    msalContext.msalExec(messengerRequest, (accessToken) => {
      if (config) {
        dispatch(
          getConfig({
            systemId: system.deviceId,
            data: {
              ...config,
              CUSTOMER_ID: site.customerId,
              DEVICE_ID: system.deviceId,
              CONFIG_FILE: system.config,
              accessToken,
              user: { homeAccountId: msalContext.user.homeAccountId },
            },
          })
        );
      }
    });

    return () => {
      dispatch(closeSocket({ systemId: system.deviceId }));
    };
  }, []);

  // this handles the socket messages and dispatches the socket data as bundles to redux
  const processAsRawSensorData = true; // with this we skip the complicated frontned data processing and just store the raw data from the socket to redux
  useSocketConnectionDevicesMessageTypeEvent(socketConnected, system.deviceId, dispatch, processAsRawSensorData);

  // GENERETE ROWS AND HEADERS
  const headCells = useMemo(() => {
    if (!sensorConfigs || sensorConfigs.length === 0) {
      return [];
    }

    // map(), handles unique ID and avoids duplicates
    const measurementMap = new Map();

    sensorConfigs.forEach((sensor) => {
      sensor.measurements.forEach((measurement) => {
        if (!measurementMap.has(measurement.valueName)) {
          measurementMap.set(measurement.valueName, {
            id: measurement.valueName,
            numeric: true,
            disablePadding: false,
            label: measurement.name,
          });
        }
      });
    });

    return [
      { id: "name", numeric: false, disablePadding: false, label: "Name" },
      ...Array.from(measurementMap.values()),
      { id: "tag", numeric: false, disablePadding: false, label: "Tag" },
    ];
  }, [sensorConfigs]);

  const generateRows = () => {
    if (!sensorConfigs || sensorConfigs.length === 0) return [];

    return sensorConfigs.map((sensor) => {
      const row = {
        hwId: sensor.hwId,
        name: sensor.name,
        tag: sensor.tag,
      };

      // Add measurement objects per measurement type
      if (rawSensorData && rawSensorData.length > 0) {
        // Add measurements to the row
        sensor.measurements.forEach((measurement) => {
          const rawData = rawSensorData.find(
            (data) => data.HwId === sensor.hwId && data.ValueName === measurement.valueName
          );

          if (rawData) {
            const floatPrecision = measurement.floatPrecision ?? 2;
            row[measurement.valueName] = {
              value: parseFloat(rawData.Value).toFixed(floatPrecision),
              unit: rawData.Unit || measurement.unit || "",
              timestamp: rawData.ValueTimestamp || null,
            };
          } else {
            // if no raw data found, initialize with null values
            row[measurement.valueName] = {
              value: null,
              unit: measurement.unit || "",
              timestamp: null,
            };
          }
        });
      }

      return row;
    });
  };

  useEffect(() => {
    const rows = generateRows();
    setDataRows(rows);
  }, [sensorConfigs, rawSensorData]);

  // SEND REFRESHSIGNALR's POST REQUESTS TO REFRESH DATA
  useEffect(() => {
    if (!sensorConfigs || sensorConfigs.length === 0) return;

    const delay = 3000;

    const refresh = async () => {
      for (const sensor of sensorConfigs) {
        const url = config.STREAMER_URL;
        const payload = {
          hwId: sensor.hwId,
          SiteId: site.customerId,
          EdgeUnitId: system.deviceId,
        };

        try {
          const response = await RefreshSignalR.post(url, payload);
          // console.log("RefreshSignalR response", response);
        } catch (error) {
          console.error("Error in RefreshSignalR.post:", error);
        }
      }
    };

    const timeout = setTimeout(() => {
      refresh();
    }, delay);

    return () => clearTimeout(timeout); // Clean up timeout on unmount or deps change
  }, [sensorConfigs]);

  // GET ALARMS

  useEffect(() => {
    if (!socketConnected || !Socket) return;

    const alarmListener = (res) => {
      if (res && system.deviceId === res.DeviceId) {
        setAlarms((prevAlarms) => {
          const exists = prevAlarms.some((alarm) => alarm.AlarmId === res.AlarmId);
          if (exists) {
            console.warn("Alarm already exists:", res.AlarmId);
            return prevAlarms;
          }
          return [...prevAlarms, res];
        });
      }
    };

    Socket.on(alarmsMessageTypeEvent, alarmListener, system.deviceId);

    return () => {
      Socket.removeListener(alarmsMessageTypeEvent, alarmListener, system.deviceId);
    };
  }, [socketConnected, system.deviceId]);


  //// Generate measurement name options for highlight filtering
  useEffect(() => {
    if (!sensorConfigs || sensorConfigs.length === 0) return;

    const uniqueMeasurements = measurementNameList;

    sensorConfigs.forEach((sensor) => {
      sensor.measurements.forEach((measurement) => {
        if (!uniqueMeasurements.some((m) => m.value === measurement.valueName)) {
          uniqueMeasurements.push({
            value: measurement.valueName,
            label: measurement.name,
          });
        }
      });
    });

    // Sort the list by label
    uniqueMeasurements.sort((a, b) => a.label.localeCompare(b.label));
    handleChangeMeasurementList(uniqueMeasurements);
  }, [sensorConfigs]);

  // CHECK IF ALARM IS PRESENT FOR CELL, RETURN THE ALARM OBJECT IF PRESENT
  const getAlarmForCell = (hwId, valueName) => {
    const alarm = alarms.find((alarm) => alarm.HwId === hwId && alarm.ValueName === valueName);
    return alarm || null;
  };

  const checkIfDeviceHasAlarmsAndReturnHighestSeverityLevel = (sensorWithData) => {
    let highestSeverity = 0;
    Object.keys(sensorWithData).some((key) => {
      if (key === "hwId" || key === "name" || key === "tag") return false; // Skip non-measurement keys
      const alarm = getAlarmForCell(sensorWithData.hwId, key);
      const severity = Math.abs(alarm?.SeverityValue) || 0;
      highestSeverity = Math.max(highestSeverity, severity);
    });
    return highestSeverity;
  };

  // const checkForAlarmsThatHaveNoCorrespondingValueNameInTable = (sensorWithData) => {
  //   // sensorWithData has key value pairs, key is the measurement valueName
  //   const otherAlarms = alarms.filter((alarm) => {
  //     return (
  //       alarm.HwId === sensorWithData.hwId &&
  //       !Object.keys(sensorWithData).includes(alarm.ValueName) &&
  //       alarm.ValueName !== "name" &&
  //       alarm.ValueName !== "tag"
  //     );
  //   });
  //   return otherAlarms;
  // };

  const alarmColor = (severity) => {
    let severityAbs = Math.abs(severity);
    switch (severityAbs) {
      case 0:
        return "text.primary"; // No alarm
      case 1:
        return "warning"; // Warning
      case 2:
        return "error"; // Critical
      default:
        return "text.primary";
    }
  };

  const tooltipAlarms = (sensorWithData) => {
    const activeAlarms = alarms.filter((alarm) => alarm.HwId === sensorWithData.hwId && alarm.TurnedOffTimeStamp === 0);
    if (activeAlarms?.length === 0) {
      return "No active alarms";
    }
    return (
      <Stack spacing={1}>
        {activeAlarms.map((alarm) => (
          <Stack key={alarm.AlarmId} direction="row" spacing={1} alignItems="center">
            <WarningIcon fontSize="small" color={alarmColor(alarm.SeverityValue)} />
            <Typography color={alarmColor(alarm.SeverityValue)} sx={{ fontSize: "14px" }}>
              {i18n.language === "fi" ? alarm.DescriptionFI : alarm.DescriptionEN}
            </Typography>
          </Stack>
        ))}
      </Stack>
    );
  };

  // const tooltipOtherAlarms = (otherAlarms) => {
  //   if (otherAlarms.length === 0) {
  //     return "No other alarms";
  //   }
  //   console.log("otherAlarms", otherAlarms);
  //   return (
  //     <Stack spacing={1}>
  //       {otherAlarms.map((alarm) => (
  //         <Stack key={alarm.AlarmId} direction="row" spacing={1} alignItems="center">
  //           <WarningIcon fontSize="small" color={alarmColor(Math.abs(alarm.SeverityValue))} />
  //           <Typography color={alarmColor(Math.abs(alarm.SeverityValue))} sx={{ fontSize: "14px" }}>
  //             {alarm.DescriptionEN}
  //           </Typography>
  //         </Stack>
  //       ))}
  //     </Stack>
  //   );
  // };

  // TABLE ORDER FUNCTIONS
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedRows = useMemo(() => {
    return [...dataRows].sort(getComparator(order, orderBy));
  }, [order, orderBy, dataRows]);

  return (
    <Box>
      <TableContainer sx={{ padding: 1 }}>
        <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
          <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} headCells={headCells} />
          <TableBody>
            {sortedRows.length > 0 &&
              sortedRows.map((sensorWithData) => (
                <TableRow key={sensorWithData.hwId} hover tabIndex={-1}>
                  {headCells.map((headCell) => {
                    const cellData = sensorWithData[headCell.id];
                    const alarm = getAlarmForCell(sensorWithData.hwId, headCell.id);
                    const highestActiveAlarmSeverity = checkIfDeviceHasAlarmsAndReturnHighestSeverityLevel(sensorWithData);

                    // true or false
                    const isCellHighlighted = () => {
                      if (!sensorValueFilterMeasurement || !sensorValueFilterValue) return false;
                      if (sensorValueFilterMeasurement.replace(/\s/g, "").toLowerCase() === headCell.id.replace(/\s/g, "").toLowerCase()) {
                        if (sensorValueFilterOperator === "greaterThan") {
                          return parseFloat(cellData.value) > parseFloat(sensorValueFilterValue);
                        } else if (sensorValueFilterOperator === "lessThan") {
                          return parseFloat(cellData.value) < parseFloat(sensorValueFilterValue);
                        }
                      }
                      return false;
                    }


                    return (
                      <TableCell key={headCell.id} padding="none"  >
                        {headCell.id === "name" ? (
                          <Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                              {highestActiveAlarmSeverity !== 0 && (
                                <Tooltip title={tooltipAlarms(sensorWithData)} placement="top-start">
                                  <WarningIcon fontSize="small" color={alarmColor(highestActiveAlarmSeverity)} />
                                </Tooltip>
                              )}
                              {/* {otherAlarms.length > 0 && (
                                <Tooltip title={tooltipOtherAlarms(otherAlarms)} placement="top-start">
                                  <WarningIcon fontSize="small" color={alarmColor(highestActiveAlarmSeverity)} />
                                </Tooltip>
                              )} */}
                              <Typography color={alarmColor(highestActiveAlarmSeverity)} sx={{ fontSize: "14px" }}>
                                {sensorWithData.name}
                              </Typography>
                            </Stack>
                            {showSensorId && (
                              <Typography variant="caption" color="textSecondary">
                                {sensorWithData.hwId}
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          typeof cellData === "object" &&
                          cellData !== null && (
                            <Stack direction="column" spacing={0.5} >
                              <Stack direction="row" spacing={1}
                                //highlight if value matches filter
                                sx={{
                                  border: isCellHighlighted() ? `1px solid ${sensorTable.highlightBorder}` : "1px solid transparent",
                                  backgroundColor: isCellHighlighted() ? sensorTable.highlightBg : "transparent",
                                  padding: "2px 4px",
                                  borderRadius: "4px",
                                }}

                              >
                                <Typography
                                  color={alarm ? alarmColor(alarm.SeverityValue) : "text.primary"}
                                  sx={{ fontSize: "14px" }}
                                >
                                  {cellData.value}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {cellData.unit}
                                </Typography>
                              </Stack>
                              {showTimestamp && cellData.timestamp && (
                                <Typography variant="caption" color="textSecondary">
                                  {new Date(cellData.timestamp).toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </Typography>
                              )}
                            </Stack>
                          )
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SensorTable;
